import { useState, useEffect, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';

import { EditIcon } from '@chakra-ui/icons';

import type {
  APILeadsListResultData,
  APILeadOverviewData,
  APILeadUpdateData,
} from 'types/api.types';

import { apiUpdateLead } from 'api';

const LeadAddNote = ({
  lead,
  onSuccess,
}: {
  lead: APILeadsListResultData | APILeadOverviewData;
  onSuccess?: (data: APILeadUpdateData) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [notes, setNotes] = useState(lead.notes);
  const [originalNotes, setOriginalNotes] = useState(lead.notes);
  const toast = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (noteText: string) => {
      return apiUpdateLead(lead.id, { notes: noteText });
    },
    onSuccess: (data) => {
      toast({
        title: 'Notes updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      onClose();

      if (onSuccess) {
        onSuccess(data);
      } else {
        lead.notes = data.notes;
      }
    },
    onError: (error) => {
      toast({
        title: 'Error updating notes',
        description: 'There was a problem saving your notes',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  useEffect(() => {
    setNotes(lead.notes);
    setOriginalNotes(lead.notes);
  }, [lead.notes]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      const focusTimeout = setTimeout(() => {
        if (textareaRef.current) {
          const textarea = textareaRef.current;
          textarea.focus();

          const length = textarea.value.length;
          textarea.selectionStart = length;
          textarea.selectionEnd = length;

          textarea.scrollTop = textarea.scrollHeight;
        }
      }, 150);

      return () => clearTimeout(focusTimeout);
    }
  }, [isOpen, notes]);

  const handleSave = () => {
    if (notes !== originalNotes) {
      mutate(notes);
    } else {
      onClose();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };

  const handleCancel = () => {
    setNotes(lead.notes);
    onClose();
  };

  const handleModalOpen = () => {
    const currentDateTime = new Date().toLocaleString();
    const existingNotes = lead.notes || '';

    if (existingNotes.trim() === '') {
      setNotes(`${currentDateTime}\n\n`);
    } else {
      setNotes(`${existingNotes}\n\n${currentDateTime}\n\n`);
    }

    setOriginalNotes(lead.notes || '');
    onOpen();
  };

  return (
    <>
      <Tooltip label="Add/Edit Notes" hasArrow placement="top">
        <IconButton
          aria-label="Add or edit notes"
          icon={<EditIcon />}
          size="sm"
          colorScheme="teal"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleModalOpen();
          }}
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{lead.notes ? 'Edit Notes' : 'Add Notes'}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Textarea
              ref={textareaRef}
              variant="main"
              value={notes}
              onChange={handleChange}
              placeholder="Enter notes about this lead..."
              size="md"
              minH="500px"
              resize="vertical"
              autoFocus
              onFocus={(e) => {
                const length = e.target.value.length;
                e.target.selectionStart = length;
                e.target.selectionEnd = length;
              }}
            />
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={isPending}
              loadingText="Saving"
            >
              Save Notes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default LeadAddNote;
